<template>
	<div id="app">
		<div v-if="!requiredResourcesAreReady || redirectInPending" class="loader">
			<div class="db-spinner"></div>
		</div>
		<FunnelContainer v-if="requiredResourcesAreReady">
			<ThemeProvider>
				<router-view />
			</ThemeProvider>
		</FunnelContainer>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import config from '@/config'
import { ENV_DEV_OR_STAGING } from '@/helpers/environment'

import experiments from '@/mixins/Experiments'

import FunnelContainer from '@/components/FunnelContainer.vue'
import ThemeProvider from '@/components/ThemeProvider.vue'

export default {
	name: 'App',

	mixins: [experiments],

	components: {
		FunnelContainer,
		ThemeProvider
	},

	computed: {
		...mapGetters({
			countryCode: 'getCountryCode',
			gdpr: 'getGdprStatus',
			requiredResourcesAreReady: 'status/requiredResourcesAreReady',
			coreFeaturesResourceIsReady: 'status/coreFeaturesResourceIsReady',
			getQueryParam: 'location/getQueryParam',
			onetrustActiveGroups: 'onetrust/getActiveGroups',
			isOnetrustBannerAutoClosable: 'onetrust/isBannerAutoClosable',
			amplitudeWhiteList: 'coreFeatures/FEAT_AMPLITUDE_WHITE_LIST',
			amplitudeBlackList: 'coreFeatures/FEAT_AMPLITUDE_BLACK_LIST',
			paltaWhiteList: 'coreFeatures/FEAT_PALTA_WHITE_LIST',
			paltaBlackList: 'coreFeatures/FEAT_PALTA_BLACK_LIST'
		}),
		...mapState({
			currency: 'currency',
			marketingDeduplicationEventId: 'marketingDeduplicationEventId',
			bannerClosed: 'onetrust/bannerClosed'
		}),

		redirectInPending() {
			return this.$store.getters['status/resourceIsPending']('redirect')
		},

		isOneTrustDisabled() {
			return ENV_DEV_OR_STAGING && this.getQueryParam('onetrust') === false
		}
	},

	watch: {
		$route() {
			window.uetq?.push?.('event', 'page_view', { page_path: window.location.pathname })
			window.fbq?.('track', 'PageView')

			this.$analytic.updatePersistedProperties({ url: window.location.href })

			// Try to close OneTrust banner for non-EU users with non-blocking consent rules
			if (this.isOnetrustBannerAutoClosable && !this.$route.meta.isLanding) {
				window.OneTrust?.AllowAll?.()
			}
		},
		networkResourceIsReady(value) {
			if (value) {
				this.initAnalyticDestinationPlugins()
			}
		}
	},

	created() {
		window.ot.startSpan?.('app')

		this.$store.commit('location/storeQueryParams')

		this.$store.dispatch('location/defineOrganicTrafficFlag')

		this.handleOnetrustEvents()
		this.setEventIdToGTMDataLayer()
	},

	updated() {
		window.ot.endSpan?.('app')
	},

	methods: {
		setOnetrustData() {
			const domainData = window.OneTrust.GetDomainData?.() || {}
			const activeGroups = window.OnetrustActiveGroups.split(',').filter(Boolean) || []
			const bannerClosed = window.OneTrust.IsAlertBoxClosed()
			const optInModel = domainData.ConsentModel?.Name === 'opt-in'

			this.$store.commit('onetrust/setActiveGroups', activeGroups)
			this.$store.commit('onetrust/setBannerClosed', bannerClosed)
			this.$store.commit('onetrust/setOptInModel', optInModel)
			this.$store.commit('onetrust/setInited', true)

			// Possibility to turn off the OneTrust popup for autotests using `?onetrust=false` query param
			if (this.isOneTrustDisabled && !this.bannerClosed) {
				window.OneTrust.AllowAll()
			}
		},

		handleOnetrustEvents() {
			if (window.OneTrust?.GetDomainData?.().IsBannerLoaded) {
				this.setOnetrustData()
			} else {
				document.addEventListener('OnetrustReady', this.setOnetrustData)
			}

			document.addEventListener('OnetrustConsentChanged', (event) => {
				this.$store.commit('onetrust/setActiveGroups', event.detail)

				this.$analytic.logEvent('ButtonClick', {
					title: 'CookieConsent',
					groups: this.onetrustActiveGroups,
					path: ['CookieConsentBanner']
				})

				this.$analytic.setUserProperties({
					CookieConsentActiveGroups: this.onetrustActiveGroups
				})
			})
		},

		setEventIdToGTMDataLayer() {
			window.dataLayer?.push({
				event_id: this.marketingDeduplicationEventId
			})
		},

		initAnalyticDestinationPlugins() {
			this.$analytic.initDestinationPlugins([
				{
					instanceOptions: {
						apiKey: config('Amplitude'),
						serverUrl: 'https://api2.amplitude.com/2/httpapi'
					},
					whiteList: this.amplitudeWhiteList,
					blackList: this.amplitudeBlackList
				},
				{
					instanceOptions: {
						apiKey: config('AmplitudePaltaWeb'),
						serverUrl: 'https://telemetry.simple.paltabrain.com/v2/amplitude'
					},
					whiteList: this.paltaWhiteList,
					blackList: this.paltaBlackList
				}
			])

			// TODO: Find me a better place
			this.$analytic.setUserProperties({
				countryCode: this.countryCode,
				currency: this.currency,
				gdpr: this.gdpr
			})
		}
	}
}
</script>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
@import '@/styles/style.scss';

#app {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
}

.loader {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: var(--background-color);
	z-index: 9;
	display: flex;
	justify-content: center;
	align-items: center;
}
</style>
